import { inject, Injectable } from '@angular/core';
import { LocalStorageDriver } from './storage-drivers';

export enum StorageKeys {
  Token = 'token',
  Subscriber = 'subscriber',
  ESims = 'esims',
  SelectedSim = 'selected-sim',
  SelectedPlan = 'selected-plan',
  CheckoutData = 'checkout-data',
  EsimData = 'esim-data',
  EsimCheckoutData = 'esim-checkout-data',
  EsimInstallationData = 'esim-installation-data',
  SelectedCurrency = 'selected-currency',
  SelectedLanguage = 'selected-language',
  AdditionalPaymentRequired = 'additional-payment-required',
}

// Interface for general checkout data (without eSIM details)
export interface CheckoutData {
  regionType: string;
  destinationSlug: string;
  planSlug: string;
  redirectUrl: string;
  imsiId: string;
}

// Interface for eSIM-specific data
export interface EsimData {
  esimId: string | null;
  iccid: string | null;
  imsiId: string | null;
  name: string | null;
  isEsimSelected: boolean;
  topUpSubscriptionId?: string | null;
  isNewPlan?: boolean;
  isBuyAgain?: boolean;
  isNewSim?: boolean;
}

@Injectable({ providedIn: 'root' })
export class StorageService {
  protected driver = inject(LocalStorageDriver);

  /**
   * Sets the value against the key but auto serializing
   * @param key
   * @param value
   * @returns
   */
  public setKey<Value = unknown>(key: StorageKeys, value: Value): void {
    return this.driver.setItem(key, JSON.stringify(value));
  }

  /**
   * Returns the value stored against the key or null
   * @param key
   * @returns
   */
  public getKey<Value = unknown>(key: StorageKeys): null | Value {
    const value = this.driver.getItem<string>(key);

    if (!value) {
      return null;
    }

    return JSON.parse(value);
  }

  /**
   * Sets general checkout data (region, ID, plan, URL)
   * @param checkoutData
   */
  public setCheckoutData(checkoutData: CheckoutData): void {
    this.setKey(StorageKeys.CheckoutData, checkoutData);
  }

  /**
   * Sets eSIM-related data (esimId, iccid, imsiId, name)
   * @param esimData
   */
  public setEsimData(esimData: EsimData): void {
    this.setKey(StorageKeys.EsimData, esimData);
  }

  /**
   * Gets general checkout data (region, ID, plan, URL)
   * @returns CheckoutData or null
   */
  public getCheckoutData(): CheckoutData | null {
    return this.getKey(StorageKeys.CheckoutData);
  }

  /**
   * Gets eSIM-related data (esimId, iccid, imsiId, name)
   * @returns EsimData or null
   */
  public getEsimData(): EsimData | null {
    return this.getKey(StorageKeys.EsimData);
  }

  /**
   * Clears both checkout and eSIM data
   */
  public clearCheckoutData(): void {
    this.removeKey(StorageKeys.CheckoutData);
    this.removeKey(StorageKeys.EsimData);
  }

  /**
   * Removes the value stored in the key
   * @param key
   * @returns
   */
  public removeKey(key: StorageKeys): void {
    return this.driver.removeItem(key);
  }

  /**
   * Removes all keys
   * @returns
   */
  public clearAll(): void {
    return this.driver.clear();
  }
}
